import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFormContext } from 'react-hook-form';
import {
  defaultValuesToken,
  hiddenField,
  paramsEdit,
  paramsNext,
  profile,
  schemaToken,
  smsPreviouslyValidatedMessage,
  tokenValidStatus,
} from '../constants/form';
import { routes, siteMap } from '../constants/routes';
import { envSetting } from '../constants/envSetting';
import {
  trimLowerCase,
  notUndefinedNull
} from '../helpers/shared';
import { getValidateSms } from '../helpers/form';
import { validateRequestType } from '../helpers/formData';
import { tierMigratable } from '../helpers/tier';
import { migrateBifrost, userFilesAndMigrateBifrost } from '../redux/actions/tier'
import { sendCheckUserTier, getRejectedFiles, getUserImages } from '../redux/actions/files'
import { getPersonData, personData, setAvatarFromPersonData, updatePersonData } from '../redux/actions/personData'
import { sendEditionToken, sendEmailToken, sendSmsToken, smsToken, validateEditionToken, validateEmailToken, validateSmsToken } from '../redux/actions/token'
import { setLoader, setDelay, setNavigate, setTier } from '../redux/actions/shared'
import { setLogout } from '../redux/actions/user'
import { setMessage } from '../redux/actions/message'
import { TIER_CATEGORY } from '../redux/types/tier';
import { USER_VALIDATED_PHONE } from '../redux/types/user';
import IdleTimer from '../helpers/IdleTimer';
import { phoneTakenMessage } from '../constants/form';
import { useWatch } from 'react-hook-form';
import { useSwitchDesign } from './useSwitchDesign';
import { isLendecyApp } from '../helpers/base';
import { saveUserImage } from '../redux/actions/files';

/**
 * Hook to get the values to CardModal
 * @param {String} codeModal
 * @param {Object} setModalOpen
 * @returns
 */
export const useTokenProfile = (codeModal, setModalOpen, setIsEditing = null, setEntries = null, setIsTouched = null) => {
  const dispatch = useDispatch();
  const minutesToken = envSetting.minutesToken;
  const { t } = useTranslation();
  const { moduleProfile, userIdBifrost, lastProductRequestId, currentlyValidatingPhone } = useSelector(state => state.user);
  const { loading } = useSelector(state => state.base);
  const { register, formState: { errors }, handleSubmit, reset } = useForm({
    resolver: yupResolver(schemaToken),
    defaultValues: defaultValuesToken
  });
  const [message, setMessageToken] = useState('');

  useEffect(() => {
    if (codeModal === profile.requestEdit) {
      setMessageToken(t("profile.requestEdit"));
    }
    if (codeModal === profile.requestTokenForce || codeModal === profile.validateOne) {
      if (moduleProfile === profile.cardPhone) {
        setMessageToken(t("profile.requestTokenForcePhone"));
      }
      if (moduleProfile === profile.cardEmail) {
        setMessageToken(t("profile.requestTokenForceEmail"));
      }
    }
  }, [codeModal, moduleProfile]);

  const messageExpired = () => {
    setModalOpen(false);
    dispatch(setMessage(t("common.tokenExpired"), 'error'));
  };

  const resendToken = () => {
    reset();
    if (codeModal === profile.requestEdit) {
      dispatch(setLoader(true));
      dispatch(sendEditionToken({
        'bifrost_user_id': userIdBifrost
      }))
        .then(response => (response) && document.getElementById(
          profile.resetTimer).click())
        .catch(error => console.error('resendToken in useTokenProfile: ', error));
      dispatch(setLoader(false));
    }

    if (codeModal === profile.requestTokenForce) {
      if (moduleProfile === profile.cardPhone) {
        dispatch(setLoader(true));
        const phoneNumber = document.querySelector(
          `[name="${profile.phoneNumber}"]`).value;
        const phoneNumberCountry = document.querySelector(
          `[name="${profile.phoneNumberCountry}"]`).value;
        dispatch(smsToken(phoneNumberCountry, phoneNumber, userIdBifrost))
          .then(response => (response) && document.getElementById(
            profile.resetTimer).click())
          .catch(error => console.error('resendToken in useTokenProfile: ', error));
        dispatch(setLoader(false));
      }
      if (moduleProfile === profile.cardEmail) {
        dispatch(setLoader(true));
        const email = document.querySelector(`[name="${profile.email}"]`).value;
        dispatch(sendEmailToken({
          email,
          bifrost_user_id: userIdBifrost
        }))
          .then(response => (response) && document.getElementById(
            profile.resetTimer).click())
          .catch(error => console.error('resendToken in useTokenProfile: ', error));
        dispatch(setLoader(false));
      }
    }

    if (codeModal === profile.validateOne) {
      const phone = currentlyValidatingPhone.phone
      let phoneNumber = phone.replaceAll('-', '').replaceAll('_', '')
      dispatch(sendSmsToken({
        'phone_number': phoneNumber,
        'country_code': phone?.country_code,
        'bifrost_user_id': userIdBifrost,
        'main': phone?.main
      }))
        .then(response => (response) && document.getElementById(
          profile.resetTimer).click())
        .catch(error => console.error('resendToken in useTokenProfile: ', error))
    }
  };

  const handleFields = valid => {
    let moduleForm, editModule, editModulePhones;

    if (moduleProfile === profile.cardEmail) {
      moduleForm = document.getElementById(profile.moduleFormEmail);
      editModule = document.getElementById(profile.editModuleEmail);
    }
    if (moduleProfile === profile.cardPhone) {
      moduleForm = document.getElementById(profile.moduleFormPhone);
      editModule = document.getElementById(profile.editModulePhone);
      editModulePhones = document.getElementById(profile.editModulePhones)
    }

    if (valid) {
      if (codeModal === profile.requestEdit) {
        if (moduleProfile === profile.cardPhone) editModulePhones.classList.add('d-none')
        editModule.classList.add('d-none');
        moduleForm.classList.remove('d-none');
      }
      if (codeModal === profile.requestTokenForce) {
        moduleForm.classList.add('d-none');
        editModule.classList.remove('d-none');
        if (moduleProfile === profile.cardPhone) editModulePhones.classList.remove('d-none')
        dispatch(getPersonData(userIdBifrost, lastProductRequestId));
        dispatch(setAvatarFromPersonData(personData));
      }
      setModalOpen(false);
    } else {
      if (codeModal === profile.requestEdit) {
        moduleForm.classList.add('d-none');
        editModule.classList.remove('d-none');
        if (moduleProfile === profile.cardPhone) editModulePhones.classList.remove('d-none')
      }
      if (codeModal === profile.requestTokenForce) {
        if (moduleProfile === profile.cardPhone) editModulePhones.classList.add('d-none')
        editModule.classList.add('d-none');
        moduleForm.classList.remove('d-none');
      }
    }
  };

  const onSubmit = async data => {
    if (codeModal === profile.requestEdit) {
      dispatch(setLoader(true));
      reset();
      dispatch(validateEditionToken({
        'token': data.token,
        'bifrost_user_id': userIdBifrost
      }))
      // * IMPORTANT
        .then(({ error }) => {
          if (setIsEditing && !error && (moduleProfile === profile.cardPhone)) setIsEditing(true)
          handleFields(!error) 
        })
        .catch(error => handleFields(error));
      dispatch(setLoader(false));
    }
    if (codeModal === profile.requestTokenForce) {
      reset();
      dispatch(setLoader(true));
      if (moduleProfile === profile.cardPhone) {
        dispatch(validateSmsToken({
          'token': data.token,
          'bifrost_user_id': userIdBifrost
        }))
          .then(({ error }) => handleFields(!error))
          .catch(error => handleFields(error));
      }
      if (moduleProfile === profile.cardEmail) {
        dispatch(validateEmailToken({
          'token': data.token,
          'bifrost_user_id': userIdBifrost
        }))
          .then(({ error }) => handleFields(!error))
          .catch(error => handleFields(error));
      }
      dispatch(setLoader(false));
    }
    //* When validating an individual phone number in Profile
    if (codeModal === profile.validateOne) {
      reset()
      dispatch(setLoader(true))
      const phone = currentlyValidatingPhone.phone
      let phoneNumber = phone?.description?.replaceAll('-', '')?.replaceAll('_', '')
      const response = await dispatch(validateSmsToken({
        'bifrost_user_id': userIdBifrost,
        'country_code': phone?.country_code,
        'phone_number': phoneNumber,
        'main': phone?.main,
        'token': data?.token
      }));
      if(response.errors) {
        dispatch(setLoader(false));
        return
      } else{
        setModalOpen(false);
        setIsTouched(true);
        setEntries(prevEntries => {
          return prevEntries.map(entry => {
            if (entry?.key === currentlyValidatingPhone.phone?.key) {
              return {
                ...entry,
                validated: true
              }
            } else return entry
          })
        })
        if(currentlyValidatingPhone?.submitting){
          //* If we were submitting, before validating, continue submitting
          dispatch(setLoader(false))
          await new Promise(resolve => setTimeout(resolve, 333))
          const submitBtn = document.getElementById('btn_save_additional_phones') 
          if (submitBtn) submitBtn.click()
        }
      }
      dispatch(setLoader(false))
    }
  };

  return {
    errors,
    handleSubmit,
    loading,
    message,
    messageExpired,
    minutesToken,
    onSubmit,
    register,
    resendToken
  };
};

/**
 * Token to tier
 * @param {Boolean}  openModal
 * @param {Object}   tier
 * @param {Object}   tierFieldState
 * @param {Function} setOpenModal
 * @returns
 */
export const useTokenTier = (openModal, tier, tierFieldState, setOpenModal) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getValues, setValue } = useFormContext();
  const { t, i18n } = useTranslation();
  const { requestLanguage, location } = useSelector(state => state.base);
  const { nextTier, action } = useSelector(state => state.tier);
  const { field: fields, country: countries } = useSelector(state => state.staticData[
    `data_${trimLowerCase(requestLanguage)}`]);
  const { data: { id: userId } } = useSelector(state => state.personData);
  const { isValidatedPhone, lastProductRequestId, userIdBifrost } = useSelector(state => state.user);
  const [loading, setLoading] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const tokenSms = fields.filter(item =>
    trimLowerCase(item.unique_name) === 'modal__token').pop();
  const [mainValidated, setMainValidated] = useState(false)
  const [mainPhoneNumber, setMainPhoneNumber] = useState(null)
  const [additionalPhones, setAdditionalPhones] = useState(null)
  const { isNewDesign } = useSwitchDesign()
  const { lastTier } = useSelector(state => state.tier)

  /**
   * Only performs call to POST personData and nothing else. Only used for checking errors. 
   * @returns 
   */
  const checkPersonData = async () => {
    const values = getValues()
    const formObject = validateRequestType(values, tierFieldState, userId);
    if ((formObject.isElement || formObject.isMedia) || action === paramsEdit) {
      dispatch(setLoader(true));
      formObject.elements['country_code'] = location;
      const response = await dispatch(personData(
        formObject, userIdBifrost, lastProductRequestId))
      dispatch(setLoader(false));
      return response
    }
  }

  const reUpdatePersonData = async () => {
    await dispatch(updatePersonData(userIdBifrost, lastProductRequestId)) 
    await dispatch(getUserImages(userIdBifrost))
  }

  /**
   * Full call to POST personData, performed having successfully validated all of the phone numbers
   * @returns 
   */
  const postPersonData = async () => {
    dispatch({ type: USER_VALIDATED_PHONE, payload: true });
    const values = getValues()
    const formObject = validateRequestType(values, tierFieldState, userId);
    const migratable = tierMigratable(formObject.migratable, tier, false);
    let saveFormMedia = true;
    if (formObject.isElement || formObject.isMedia) {
      dispatch(setLoader(true));
      formObject.elements['country_code'] = location;
      const response = await dispatch(personData(
        formObject, userIdBifrost, lastProductRequestId));
      if (response && !response.error) {
        if (nextTier?.verification_level) {
          if (tier.verification_level > nextTier.verification_level) dispatch(
            setTier(tier));
        } else {
          dispatch(setTier(tier));
        }
        // ? Path to take if the 'newDesign' for the 'speed_form' tier is active/true
        if (isNewDesign) {
            const result = await dispatch(userFilesAndMigrateBifrost(
              userIdBifrost, migratable, userId, lastProductRequestId, formObject.formMedia))
            if (result && !result?.error) {
              if (migratable.migrate){
                // This call currently reupdates all of the tiers (intermediate, deeper and advanced) /
                setTimeout(() => reUpdatePersonData(), 20000)
              } 
            }
            if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
              await dispatch(sendCheckUserTier());
                    dispatch(getRejectedFiles());
                    // dispatch(setLoader(false));
                    dispatch(setDelay(false));
                    setLoading(false);
                    setShowTimer(false);
                    navigate(routes.completed(paramsNext))
            }
          
          //   if (formObject.isMedia && !isLendecyApp()) {
          //     dispatch(setDelay(true));
          //     saveFormMedia = await dispatch(saveUserImage(
          //       userIdBifrost, formObject.formMedia, false, false));
          //   }
          //   if (saveFormMedia) {
          //     let result = await dispatch(migrateBifrost(
          //       userIdBifrost, migratable, userId, lastProductRequestId));
          //     if (result && !result.error) {
          //       if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
          //         await dispatch(sendCheckUserTier());
          //         dispatch(getRejectedFiles());
          //         dispatch(setLoader(false));
          //         dispatch(setDelay(false));
          //         setLoading(false);
          //         setShowTimer(false);
          //         navigate(routes.completed(paramsNext));
          //       }
          //     } else {
          //       dispatch(setNavigate(false));
          //       dispatch(setLoader(false));
          //       dispatch(setDelay(false));
          //     }
          //   }
          // }
        } else {
          // ? Previous path calling just calling for token
          if (lastTier?.unique_name === 'speed_form'){
            migratable.migrate = true;
            migratable.edit = true
          } 
          let result = await dispatch(migrateBifrost(
            userIdBifrost, migratable, userId, lastProductRequestId));
          if (result && !result.error) {
            if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
              await dispatch(sendCheckUserTier());
              dispatch(getRejectedFiles());
              // dispatch(setLoader(false));
              dispatch(setDelay(false));
              setLoading(false);
              setShowTimer(false);
              navigate(routes.completed(paramsNext));
            }
          } else {
            dispatch(setNavigate(false));
            dispatch(setLoader(false));
            dispatch(setDelay(false));
          }
        }
      } else {
        if (!nextTier?.verification_level) dispatch(setTier([]));
        dispatch(setLoader(false));
        dispatch(setDelay(false));
        return response
      }
    } else {
      if (isNewDesign) {
        //? Path accesed after opening SMS modal, closing it without validating, and then opening it again
        dispatch(setLoader(true));
        const migratable = tierMigratable(formObject.migratable, tier, false);
        if (lastTier?.unique_name === 'speed_form'){
          migratable.migrate = true;
          migratable.edit = true
        } 
        const result = await dispatch(migrateBifrost(
          userIdBifrost, migratable, userId, lastProductRequestId));
        if (result && !result.error) {
          if (migratable?.migrate) {
            setTimeout(() => reUpdatePersonData(), 20000);
          }
          if (await dispatch(updatePersonData(userIdBifrost, lastProductRequestId))) {
            await dispatch(sendCheckUserTier());
            dispatch(getRejectedFiles());
            // dispatch(setLoader(false));
            dispatch(setDelay(false));
            setLoading(false);
            setShowTimer(false);
            navigate(routes.completed(paramsNext));
          }
        } else {
          dispatch(setNavigate(false));
          dispatch(setLoader(false));
          dispatch(setDelay(false));
        }
      }
    }
  }

  /* useEffect in charge of iterating and validating the additional phones */
  useEffect(() => {

    let values = getValues();
    const nameToken = Array.from(Object.keys(values)).filter(
      elem => elem.includes('token')).pop();
    if(nameToken) setValue(nameToken, '')

    const sendNewAdditionalPhoneToken = async () => {
      setShowTimer(true);
      if(additionalPhones?.length){
        try {
          let currentPhoneToValidate = additionalPhones?.[0]
          const [uniqueName, phoneNumber] = currentPhoneToValidate
          const values = getValues()
          // In order to make the validation call, we must find the corresponding country code
          const entryNumber = uniqueName.split('__id')[0].match(/\d+/)[0]
          const countryCode = Object.keys(values).filter(value => value.includes(`country${entryNumber}`))
          const countryId = values[countryCode]
          const country = countries.filter(country => Number(country.country_id) === Number(countryId))[0]
          let phone = phoneNumber.replaceAll('-', '').replaceAll('_', '')
          const response = await dispatch(sendSmsToken({
            'phone_number': phone,
            'country_id': countryId,
            'bifrost_user_id': userIdBifrost
          }));
          if (response && !response.error) {
            setShowTimer(true)
            setLoading(false)
            dispatch(setLoader(false))
          } else {

            setTimeout(() => {
              if (response?.client_error && trimLowerCase(response?.client_error).includes(phoneTakenMessage)) {
                dispatch(setMessage(t("message.errorSendSmsTokenNumberTaken"), 'info'));
              } else {
                dispatch(setMessage(t("message.errorSendSmsToken"), 'error'));
              }
              setModalIsOpen(false)
            }, 600);

            // setModalIsOpen(false)
            setShowTimer(false)
            setLoading(false)
            dispatch(setLoader(false))
          } 
        } catch (error) {
          console.error(error)
          dispatch(setMessage(t("message.errorSendToken"), 'error'))
          setShowTimer(false)
          setLoading(false)
          dispatch(setLoader(false))
          dispatch(setDelay(false))
          setModalIsOpen(false)
        }
      } else if (notUndefinedNull(additionalPhones) && Object.entries(additionalPhones)?.length === 0){
        await postPersonData()
        setModalIsOpen(false)
        setOpenModal(1)
        setShowTimer(false)
        dispatch(setLoader(false))

      }
    }


    if (mainValidated && modalIsOpen && additionalPhones) {

      setTimeout(() => {
        if(additionalPhones?.length > 0) {
          sendNewAdditionalPhoneToken()
        } else {
          if (additionalPhones?.length === 0) {
            setModalIsOpen(false)
            setTimeout(async () => {
              await postPersonData()
            }, 100);
          }
        }
      }, 1000);

    }

  }, [additionalPhones, mainValidated])

  useEffect(() => {
    if (openModal > 1) setModalIsOpen(true);
  }, [openModal])

  /* Gets the value for the main phone number to display in the modal. */
  useEffect(() => {
    const values = getValues();
    const mainPhone = Object.keys(values)?.filter(elem => elem.includes('main'))?.find(mainPhone => values[mainPhone] === true)
    const mainPhoneEntry = mainPhone?.match(/\d+/)[0]
    const mainFields = Object.keys(values)?.filter(elem => elem.split('__id')[0].includes(mainPhoneEntry))
    const mainPhoneNumber = mainFields?.filter(elem => elem.split('__id')[0].includes(`number${mainPhoneEntry}`))[0]
    setMainPhoneNumber(values[mainPhoneNumber])
  }, [loading, getValues])

  // Fixing the "Frozen value for additional phones" issue:
  // Identify keys to observe (phone numbers) 
  const allFormValues = getValues();
  const phoneFieldKeys = Object.keys(allFormValues).filter(key => 
    key.split('__id')[0].match(/phone_number\d+/)
  );
  const [phonesSnapshot, setPhonesSnapshot] = useState(null);


  const watchedPhones = useWatch({ name: phoneFieldKeys });

  useEffect(() => {
    // const values = getValues();
    const mainPhones = Object.keys(allFormValues).filter(elem => elem.includes('main'));
    const mainPhone = mainPhones?.find(mp => allFormValues[mp] === true);
    if (!mainPhone) return;

    const mainPhoneEntry = mainPhone.match(/\d+/)[0];

    // We want to get all phone numbers in the form (those with a value), except the main phone 
    // E.G:
    // {
    //   "phone_number2__id_": "+584125550101",
    //   "phone_number3__id_": "+584249990202" 
    // }
    const currentPhonesInForm = Object.keys(allFormValues)
      .filter(value => 
        value.split('__id')[0].match(/phone_number\d+/) && 
        !value.split('__id')[0].includes(mainPhoneEntry)
      )
      .reduce((prevObj, key) => {
        if (allFormValues[key] && allFormValues[key] !== hiddenField) {
          prevObj[key] = allFormValues[key];
        }
        return prevObj;
      }, {});

    const newEntries = Object.entries(currentPhonesInForm);

    // We compare the current phone numbers in the form with the snapshot we took when we opened the modal. 
    const userEditedFields = JSON.stringify(newEntries) !== JSON.stringify(phonesSnapshot);

    if (!additionalPhones || userEditedFields) {
      setAdditionalPhones(newEntries);
      setPhonesSnapshot(newEntries); // Update snapshot after syncing
    }

  }, [openModal, watchedPhones]);



  const submitModal = async () => {
    const values = getValues();
    try {
      dispatch(setLoader(true));
      const nameToken = Array.from(Object.keys(values)).filter(
        elem => elem.includes('token')).pop();
      if (values[nameToken]) {
        setLoading(true);
        let response
        if(!mainValidated){
          const mainPhone = Object.keys(values).filter(elem => elem.includes('main'))?.find(mainPhone => values[mainPhone] === true)
          const mainPhoneEntry = mainPhone.match(/\d+/)[0]
          const mainFields = Object.keys(values).filter(elem => elem.split('__id')[0].includes(mainPhoneEntry))
          const mainPhoneNumber = mainFields.filter(elem => elem.split('__id')[0].includes(`number${mainPhoneEntry}`))[0]
          const mainCountry = mainFields.filter(elem => elem.split('__id')[0].includes('country'))[0]
          const countryId = values[mainCountry]
          const country = countries.filter(country => Number(country.country_id) === Number(countryId))[0]
          let phoneNumber = values[mainPhoneNumber].replaceAll('-', '').replaceAll('_', '')
          response = await dispatch(validateSmsToken({
            'token': values[nameToken],
            'bifrost_user_id': userIdBifrost,
            'phone_number': phoneNumber,
            'country_code': country?.code,
            'main': true,
            'validated': true
          }));
          if (response.errors){
            dispatch(setLoader(false));
            setLoading(false);

            return
          }
        } else{
          const entryNumber = additionalPhones?.[0]?.[0].match(/\d+/)[0]
          const currentCountry = Object.keys(values).filter(name => name.split('__id')[0].includes(entryNumber) && name.split('__id')[0].includes('country'))
          const countryId = values[currentCountry]
          const country = countries.filter(country => Number(country.country_id) === Number(countryId))[0]
          let phoneNumber = additionalPhones[0][1].replaceAll('-', '').replaceAll('_', '')
          response = await dispatch(validateSmsToken({
            'token': values[nameToken],
            'bifrost_user_id': userIdBifrost,
            'country_code': country?.code,
            'phone_number': phoneNumber,
            'main': false,
            'validated': true
          }));
        }
        if (!response?.errors) {
          let phonesToValidate = {}
          if (!mainValidated){
            const mainPhones = Object.keys(values).filter(elem => elem.includes('main'))
            const mainPhone = mainPhones?.find(mainPhone => values[mainPhone] === true)
            const mainPhoneEntry = mainPhone.match(/\d+/)[0]
            const validatedPhones = Object.keys(values).filter(elem => elem.includes('validated'))
            const validatedPhone = validatedPhones.filter(phone => phone.split('id')[0].includes(mainPhoneEntry))[0]
            setValue(validatedPhone, true)
            setShowTimer(true);
            // Getting all secondary phone names 
            phonesToValidate = Object.keys(values).filter(value => (value.split('__id')[0].match(/phone_number\d+/) && !value.split('__id')[0].includes(mainPhoneEntry)))
            // Getting those that actually hold a value (number to validate)
            phonesToValidate = Object.keys(values).filter(value => phonesToValidate.includes(value))
              .reduce((prevObj, key) => {
                if(values[key] && values[key] !== hiddenField) prevObj[key] = values[key]
                return prevObj
              }, {})
          } else {
            const entryNumber = additionalPhones[0][0].match(/\d+/)
            const currentValidated = Object.keys(values).filter(name => name.split('__id')[0].includes(entryNumber) && name.split('__id')[0].includes('validated'))[0]
            setValue(currentValidated, true)
          }
          if (!Object.keys(phonesToValidate)?.length && ((additionalPhones === null) || (additionalPhones?.length === 0))) {
            //* If there are no more cellphone to validate, then we POST
            await postPersonData()
          } else {
            // * If there are remaining phonesToValidate
            setOpenModal(openModal + 1)
            setValue(`${tokenSms.unique_name}__id__${tokenSms.id}`, '');
            if (!mainValidated) { 
              setAdditionalPhones(Object.entries(phonesToValidate))
              setMainValidated(true)
            } else { 
              setAdditionalPhones(prevPhones => {
                let copy = [...prevPhones]
                if(prevPhones?.length) copy?.shift()
                return copy
              })
            }
          }
        } else {
          if (response?.errors && trimLowerCase(response?.errors) === smsPreviouslyValidatedMessage) {
            setMessage(t("message.phoneAlreadyValidated"), 'success')
            if(additionalPhones?.length > 1){
              setAdditionalPhones(prevPhones => {
                let copy = [...prevPhones]
                if(prevPhones?.length) copy?.shift()
                return copy
              })
            } else{
              await postPersonData()
            }
          }
        }
        setLoading(false);
        dispatch(setLoader(false));
        dispatch(setDelay(false));
      } else {
        dispatch(setMessage(t("common.mustToken"), 'error'));
        dispatch(setLoader(false));
        dispatch(setDelay(false));
      }
    } catch (error) {
      console.error('submitModal in useTokenTier: ', error);
      dispatch(setMessage(t("message.errorSubmitToken"), 'error'));
      setLoading(false);
      dispatch(setLoader(false));
      dispatch(setDelay(false));
      setModalIsOpen(false);
    }
    setShowTimer(false)
  }

  const arePhoneNumbersRepeated = () => {
    const values = getValues();

    // Filter and collect phone numbers
    const phoneNumbers = Object.keys(values).filter(key => key.split('__id')[0].match(/phone_number\d+/))
      .reduce((obj, key) => {
        if (values[key] && values[key] !== hiddenField) {
          obj[key] = values[key];
        }
        return obj;
      }, {});

    // Check for duplicates considering country prefix
    const fullPhoneNumbers = new Set();
    for (const key in phoneNumbers) {
        const index = key.match(/phone_number(\d+)/)[1]; // Extract index
        // Find the country key dynamically
        const countryKey = Object.keys(values).find(k => k.startsWith(`block${index}__user_phone_number_country${index}`));
        const countryPrefix = countryKey ? values[countryKey] : ''; // Get the prefix if the key exists
        const fullPhoneNumber = `${values[countryKey] || ''}${phoneNumbers[key]}`; // Combine prefix and number

        if (fullPhoneNumbers.has(fullPhoneNumber)) {
            return true; // Duplicate found
        }
        fullPhoneNumbers.add(fullPhoneNumber);
    }

    return false; // No duplicates
};

  const sendToken = async () => {
    dispatch(setLoader(true));
    setLoading(true);
    setShowTimer(false);
    const values = getValues();
    try {
      if (arePhoneNumbersRepeated()) {
        setLoading(false);
        dispatch(setLoader(false)); 
        setModalIsOpen(false);
        setTimeout(() => {
          dispatch(setMessage(t("message.errorRepeatedPhoneNumbers"), 'error'));
        }, 333);
        return;
      }
      const personDataResponse = await checkPersonData()
      if (personDataResponse && !personDataResponse.errors) { // ? If No errors
        if (!isValidatedPhone){
          if (!mainValidated) {
            const { phoneNumber, countryCode, countryId } = getValidateSms(values);
            if (phoneNumber && countryCode) {
              let phone = phoneNumber.replaceAll('-', '').replaceAll('_', '')
              const response = await dispatch(sendSmsToken({
                'phone_number': phone,
                'country_id': countryId,
                'bifrost_user_id': userIdBifrost
              }));
              if (response && !response.error) {
                dispatch({ type: TIER_CATEGORY, payload: [] });
                setShowTimer(true);
                setLoading(false);
                dispatch(setLoader(false));
              } else {
                setModalIsOpen(false);
                setShowTimer(false);
                setLoading(false);
                dispatch(setLoader(false));
                setTimeout(() => {
                  if (response?.client_error && trimLowerCase(response?.client_error).includes(phoneTakenMessage)) {
                    dispatch(setMessage(t("message.errorSendSmsTokenNumberTaken"), 'info'));
                  } else {
                    dispatch(setMessage(t("message.errorSendSmsToken"), 'error'));
                  }
                }, 600);
              }
            } else {
              if (!phoneNumber) console.error('Error: Missing phone number')
              if (!countryCode) console.error('Error: Missing phone country code')  
              dispatch(setMessage(t("message.errorSendSmsToken"), 'error'));
              setLoading(false);
              dispatch(setLoader(false));
              setModalIsOpen(false);
            }
          } else { 
            if (additionalPhones?.length > 0) {
              let values = getValues()
              const entryNumber = additionalPhones[0][0].match(/\d+/)
              const currentCountry = Object.keys(values).filter(name => name.split('__id')[0].includes(entryNumber) && name.split('__id')[0].includes('country'))
              const countryId = values[currentCountry]
              const country = countries.filter(country => Number(country.country_id) === Number(countryId))[0]
              let phoneNumber = additionalPhones[0][1].replaceAll('-', '').replaceAll('_', '')
  
              const additionalPhoneResponse = await dispatch(sendSmsToken({
                'country_id': countryId,
                'phone_number': phoneNumber,
                'bifrost_user_id': userIdBifrost
              }));
              setShowTimer(true);
              setLoading(false);
              dispatch(setLoader(false));
              if (additionalPhoneResponse.error) {
                if (trimLowerCase(additionalPhoneResponse?.client_error).includes(phoneTakenMessage)) {
                  dispatch(setMessage(t("message.errorSendSmsTokenNumberTaken"), 'info'));
                } else {
                  dispatch(setMessage(t("message.errorSendSmsToken"), 'error'));
                }
              } else {
                if (additionalPhoneResponse?.success && additionalPhoneResponse?.token_status === tokenValidStatus) {
                  setMessage(t("message.phoneAlreadyValidated"), 'success')
                  if (additionalPhones?.length > 1) {
                    setAdditionalPhones(prevPhones => {
                      let copy = [...prevPhones]
                      if (prevPhones?.length) copy?.shift()
                      return copy
                    })
                  } else {
                    await postPersonData()
                  }
                  
                }
              }
  
            }
          }
        } else{
          await postPersonData()
        }
      } else { // ? If errors
        const errorsArray = Object.keys(personDataResponse?.errors)
        setModalIsOpen(false);
        dispatch(setMessage(t("message.errorPersonData"), 'error'));
        if (errorsArray?.length > 0) {
          setTimeout(() => {
            const messageForError = i18n.exists(`personData.errors.${errorsArray[0]}`) ? t(`personData.errors.${errorsArray[0]}`) : 'Unknown Error'
            dispatch(setMessage(messageForError, 'error'));
          }, 1000);
        }
      }

    } catch (error) {
      setShowTimer(false);
      setLoading(false);
      dispatch(setLoader(false));
      setModalIsOpen(false);
      dispatch(setMessage(t("message.errorSendSmsToken"), 'error'));
      console.error('sendToken in useTokenTier: ', error);
    }
  }

  const skipButton = async () => {
    let values = getValues();
    dispatch(setLoader(true));
    setLoading(true);
    setShowTimer(false);
    const entryNumber = additionalPhones[0][0].match(/\d+/)
    const currentCountry = Object.keys(values).filter(name => name.split('__id')[0].includes(entryNumber) && name.split('__id')[0].includes('country'))
    const countryId = values[currentCountry]
    const country = countries.filter(country => Number(country.country_id) === Number(countryId))[0]
    let phoneNumber = additionalPhones[0][1].replaceAll('-', '').replaceAll('_', '')
    try {
      await dispatch(validateSmsToken({
        'validated': false,
        'country_code': country?.code,
        'phone_number': phoneNumber,
        'bifrost_user_id': userIdBifrost
      }));
    } catch (error) {
      console.error(error)
      dispatch(setMessage(t("message.errorSubmitToken"), 'error'));
    }

    if(additionalPhones?.length > 1){
      setAdditionalPhones(prevPhones => {
        let copy = [...prevPhones]
        if(prevPhones?.length) copy?.shift()
        return copy
      })
    } else{
      await postPersonData()
    }
  }

  return {
    loading,
    modalIsOpen,
    sendToken,
    setModalIsOpen,
    showTimer,
    submitModal,
    tokenSms,
    skipButton,
    additionalPhones,
    mainValidated,
    mainPhoneNumber,
    additionalPhones
  }
};

/**
 * Hook to auto logout
 */
export const useAutoLogout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // localStorage.removeItem('_expiredTime');

    const timer = new IdleTimer({
      timeout: envSetting.expirationTime, // expire after amount of setted seconds
      onTimeout: () => {
        dispatch(setLogout());
        navigate(siteMap.login.path2);
        dispatch(setMessage(t("common.loginToken"), 'error'));
      },
      onExpired: () => {
        // do something if expired on load
        dispatch(setLogout());
        navigate(siteMap.login.path2);
        dispatch(setMessage(t("common.loginToken"), 'error'));
      },
      onExpiredSession: () => {
        // show modal 1 minute before session expiress
        dispatch(setMessage(t("common.sessionExpired"), 'warning', true));
      }
    });

    return () => {
      timer.cleanUp();
    };
  }, []);
};